import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  IconDashboard,
  IconMasterSettings,
  IconOrganization,
  IconLocation,
  IconUsers,
  IconAsset,
  IconIoT,
  IconMapping,
  IconModbus,
  IconPLC,
  IconRegister,
  IconMonitoring,
  IconBarChart,
  IconAlert,
  IconMaintenance,
  IconTicket,
  IconWorkOrder,
  IconReports
} from '../assets/Icon/Svg'

const SideBar = () => {
  const location = useLocation()

  // 1. STATE MANAGEMENT
  const [isMasterSettingsOpen, setIsMasterSettingsOpen] = useState(false)
  const [isModbusConfigOpen, setIsModbusConfigOpen] = useState(false)
  const [isDataMonitoringOpen, setIsDataMonitoringOpen] = useState(false)

  // 2. HELPER FUNCTIONS
  // Exact match for standard links
  const isActive = path => location.pathname === path

  // Partial match helper: useful for keeping parent dropdowns highlighted
  // when a child route is currently active.
  const isChildActive = basePath => location.pathname.startsWith(basePath)

  // 3. CSS CONSTANTS
  const baseItemClass =
    'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group font-medium cursor-pointer select-none'
  const activeItemClass = 'bg-blue-900 text-white shadow-sm'
  const inactiveItemClass = 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
  const childInactiveClass =
    'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
  const disabledItemClass =
    'text-gray-300 cursor-not-allowed pointer-events-none select-none'

  // 4. LIFECYCLE EFFECTS
  // Auto-expand dropdowns on initial load or URL change if we navigate directly to a child page.
  useEffect(() => {
    // Master Settings children (Organization & Locations now managed via Super Admin page)
    if (['/users'].includes(location.pathname)) {
      setIsMasterSettingsOpen(true)
    }
    // Modbus Config children
    if (location.pathname.startsWith('/modbus-config')) {
      setIsModbusConfigOpen(true)
    }
    if (
      ['/data-monitoring', '/modbus-data-monitoring'].includes(
        location.pathname
      )
    ) {
      setIsDataMonitoringOpen(true)
    }
  }, [location.pathname])

  return (
    <aside className='w-64 bg-white h-screen border-r border-gray-200 flex flex-col sticky top-0 overflow-y-auto font-sans text-sm'>
      {/* --- LOGO / HEADER --- */}
      <div className='h-16 flex items-center px-6 border-b border-gray-100 mb-4'>
        <span className='text-lg font-bold text-gray-800'>QEDGE</span>
      </div>

      {/* --- NAVIGATION MENU --- */}
      <div className='flex flex-col gap-1 px-3 pb-6'>
        {/* Dashboard */}
        <Link
          to='/dashboard'
          className={`${baseItemClass} ${
            isActive('/dashboard') ? activeItemClass : inactiveItemClass
          }`}
        >
          {IconDashboard}
          <span>Dashboard</span>
        </Link>

        {/* Advanced Dashboard */}
        <Link
          to='/advanced-dashboard'
          className={`${baseItemClass} ${
            isActive('/advanced-dashboard') ? activeItemClass : inactiveItemClass
          }`}
        >
          {IconBarChart}
          <span className='font-medium'>Advanced Dashboard</span>
        </Link>

        {/* --- MASTER SETTINGS DROPDOWN --- */}
        <div>
          <div
            onClick={() => setIsMasterSettingsOpen(!isMasterSettingsOpen)}
            className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors cursor-pointer select-none ${
              ['/users'].includes(location.pathname)
                ? activeItemClass
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className='flex items-center gap-3'>
              {IconMasterSettings}
              <span className='font-semibold'>Master Settings</span>
            </div>

            <svg
              xmlns='http://www.w3.org/2000/svg'
              className={`w-4 h-4 transition-transform duration-200 ${
                isMasterSettingsOpen ? 'rotate-180' : ''
              }`}
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth='2'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M19 9l-7 7-7-7'
              />
            </svg>
          </div>

          {/* Submenu — Organization & Locations moved to Super Admin page */}
          {isMasterSettingsOpen && (
            <div className='relative pl-9 mt-1 space-y-1'>
              {/* Vertical connecting line */}
              <div className='absolute left-6 top-0 bottom-2 w-px bg-gray-200'></div>

              <Link
                to='/users'
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive('/users') ? activeItemClass : childInactiveClass
                }`}
              >
                {IconUsers}
                <span className='font-medium'>Users</span>
              </Link>
            </div>
          )}
        </div>

        <Link
          to='/assets'
          className={`${baseItemClass} ${
            isActive('/assets') ? activeItemClass : inactiveItemClass
          }`}
        >
          {IconAsset}
          <span className='font-medium'>Asset Management</span>
        </Link>

        {/* IoT Controllers */}
        <Link
          to='/iot-controllers'
          className={`${baseItemClass} ${
            isActive('/iot-controllers') ? activeItemClass : inactiveItemClass
          }`}
        >
          {IconIoT} <span className='font-medium'>IoT Controllers</span>
        </Link>

        {/* Asset-IoT Mapping — not implemented */}
        {/* <div className={`${baseItemClass} ${disabledItemClass}`} title="Coming soon">
          {IconMapping} <span className="font-medium">Asset-IoT Mapping</span>
        </div> */}

        {/* --- MODBUS CONFIG — not implemented --- */}
        <div
          className={`flex items-center justify-between ${baseItemClass} ${disabledItemClass}`}
          title='Coming soon'
        >
          <div className='flex items-center gap-3'>
            {IconModbus}
            <span className='font-medium'>Modbus Config</span>
          </div>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='w-4 h-4'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            strokeWidth='2'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M19 9l-7 7-7-7'
            />
          </svg>
        </div>

        {/* Data Monitoring Dropdown */}
        <div>
          <div
            onClick={() => setIsDataMonitoringOpen(!isDataMonitoringOpen)}
            className={`flex items-center justify-between ${baseItemClass} ${
              isChildActive('/data-monitoring') ||
              isChildActive('/modbus-data-monitoring')
                ? activeItemClass
                : inactiveItemClass
            }`}
          >
            <div className='flex items-center gap-3'>
              {IconMonitoring}
              <span className='font-medium'>Data Monitoring</span>
            </div>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className={`w-4 h-4 transition-transform duration-200 ${
                isDataMonitoringOpen ? 'rotate-180' : ''
              }`}
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth='2'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M19 9l-7 7-7-7'
              />
            </svg>
          </div>

          {isDataMonitoringOpen && (
            <div className='relative pl-9 mt-1 space-y-1'>
              <div className='absolute left-6 top-0 bottom-2 w-px bg-gray-200' />
              <Link
                to='/data-monitoring'
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive('/data-monitoring')
                    ? activeItemClass
                    : childInactiveClass
                }`}
              >
                {IconMonitoring}{' '}
                <span className='font-medium'>Asset-IoT Data Monitoring</span>
              </Link>
              <div
                className={`flex items-center gap-3 px-3 py-2 rounded-lg ${disabledItemClass}`}
                title='Coming soon'
              >
                {IconBarChart}{' '}
                <span className='font-medium'>Modbus Data Monitoring</span>
              </div>
            </div>
          )}
        </div>

        <div
          className={`${baseItemClass} ${disabledItemClass}`}
          title='Coming soon'
        >
          {IconAlert}
          <span className='font-medium'>Alert Settings</span>
        </div>

        <div
          className={`${baseItemClass} ${disabledItemClass}`}
          title='Coming soon'
        >
          {IconMaintenance}
          <span className='font-medium'>Maintenance</span>
        </div>

        <div
          className={`${baseItemClass} ${disabledItemClass}`}
          title='Coming soon'
        >
          {IconTicket}
          <span className='font-medium'>Service Tickets</span>
        </div>

        <div
          className={`${baseItemClass} ${disabledItemClass}`}
          title='Coming soon'
        >
          {IconWorkOrder}
          <span className='font-medium'>Work Orders</span>
        </div>

        <Link
          to='/reports'
          className={`${baseItemClass} ${
            isActive('/reports') ? activeItemClass : inactiveItemClass
          }`}
        >
          {IconReports}
          <span className='font-medium'>Reports</span>
        </Link>
      </div>
    </aside>
  )
}

export default SideBar
